import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { NumberInputComponent } from '@examdojo/core/input';
import { MarkdownViewerComponent } from '@examdojo/core/ui/markdown-viewer';

import type { ScatterPlotPoint, ScatterPlotPrediction } from '../../charts/scatter-plot/scatter-plot-chart.model';
import { predictY } from '../../charts/scatter-plot/scatter-plot-chart.util';
import type { CorrelationHoverSection } from '../../../../dashboards/correlation.store';
import { buildCombinedCorrelationFormula, buildEquationDerivationFormula } from './correlation-measures-formula.util';
import { computeAllCorrelationMeasures, computeCorrelationIntermediates } from './correlation-measures.util';

@Component({
  selector: 'dojo-correlation-measures-panel',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatExpansionModule,
    MatTooltipModule,
    ButtonComponent,
    IconComponent,
    NumberInputComponent,
    MarkdownViewerComponent,
  ],
  templateUrl: './correlation-measures-panel.component.html',
  styleUrl: './correlation-measures-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CorrelationMeasuresPanelComponent {
  readonly points = input.required<ScatterPlotPoint[]>();
  readonly xLabel = input<string>('x');
  readonly yLabel = input<string>('y');

  readonly predictionChange = output<ScatterPlotPrediction | null>();
  readonly sectionHover = output<CorrelationHoverSection | null>();

  // ---------------------------------------------------------------------------
  // Tooltips (educational descriptions)
  // ---------------------------------------------------------------------------

  readonly rTooltip =
    "Pearson's correlation coefficient (r) measures the strength and direction of the linear relationship between two variables. It ranges from −1 (perfect negative) to +1 (perfect positive). Values close to 0 indicate no linear relationship.";

  readonly rSquaredTooltip =
    'The coefficient of determination (R²) tells you what percentage of the variation in y is explained by x. For example, R² = 0.81 means 81% of the variation in y can be predicted from x.';

  readonly equationTooltip =
    "The regression line ŷ = ax + b is the best-fit straight line through the data points. 'a' (slope) is the rate of change — how much y changes per unit increase in x. 'b' (intercept) is the predicted y-value when x = 0.";

  // ---------------------------------------------------------------------------
  // Core computed values
  // ---------------------------------------------------------------------------

  private readonly intermediates = computed(() => computeCorrelationIntermediates(this.points()));
  private readonly measures = computed(() => computeAllCorrelationMeasures(this.points()));

  readonly hasData = computed(() => this.points().length >= 2);

  // ---------------------------------------------------------------------------
  // Expand / Collapse
  // ---------------------------------------------------------------------------

  private readonly expandedSections = signal<Set<string>>(new Set());

  readonly rExpanded = computed(() => this.expandedSections().has('r'));
  readonly rSquaredExpanded = computed(() => this.expandedSections().has('rSquared'));
  readonly equationExpanded = computed(() => this.expandedSections().has('equation'));

  // ---------------------------------------------------------------------------
  // Correlation (r)
  // ---------------------------------------------------------------------------

  readonly r = computed(() => asNumber(this.measures().get('r')));

  readonly rFormatted = computed(() => {
    const r = this.r();
    const sign = r >= 0 ? '+' : '';
    return `${sign}${r.toFixed(4)}`;
  });

  readonly rStrengthLabel = computed(() => {
    const r = this.r();
    const abs = Math.abs(r);
    const dir = r >= 0 ? 'Positive' : 'Negative';
    if (abs >= 0.9) {
      return `Very Strong ${dir}`;
    }
    if (abs >= 0.75) {
      return `Strong ${dir}`;
    }
    if (abs >= 0.5) {
      return `Moderate ${dir}`;
    }
    if (abs >= 0.25) {
      return `Weak ${dir}`;
    }
    return 'No Linear Correlation';
  });

  readonly rColor = computed(() => {
    const r = this.r();
    const abs = Math.abs(r);
    if (abs < 0.25) {
      return 'var(--color-neutral-500, #6b7280)';
    } // neutral-500 — no correlation
    if (abs < 0.5) {
      return r > 0 ? '#86efac' : '#fca5a5';
    } // green-300 / red-300 — weak
    if (abs < 0.75) {
      return r > 0 ? '#22c55e' : '#ef4444';
    } // green-500 / red-500 — moderate
    return r > 0 ? '#16a34a' : '#dc2626'; // green-600 / red-600 — strong
  });

  readonly rArrowIcon = computed(() => {
    const r = this.r();
    if (Math.abs(r) < 0.25) {
      return 'minus';
    }
    return r > 0 ? 'arrow-trend-up' : 'arrow-trend-down';
  });

  readonly rCombinedFormula = computed(() => {
    if (!this.hasData()) {
      return '';
    }
    return buildCombinedCorrelationFormula('r', this.intermediates(), this.measures());
  });

  readonly correlationScale = computed(() => {
    const r = this.r();
    let zone: string;
    if (r < -0.75) {
      zone = 'strong-neg';
    } else if (r < -0.25) {
      zone = 'weak-neg';
    } else if (r <= 0.25) {
      zone = 'none';
    } else if (r <= 0.75) {
      zone = 'weak-pos';
    } else {
      zone = 'strong-pos';
    }

    const position = Math.max(0, Math.min(100, ((r + 1) / 2) * 100));
    return { zone, position };
  });

  // ---------------------------------------------------------------------------
  // R-squared
  // ---------------------------------------------------------------------------

  readonly rSquared = computed(() => asNumber(this.measures().get('rSquared')));

  readonly rSquaredFormatted = computed(() => this.rSquared().toFixed(4));

  readonly varianceExplained = computed(() => `${(this.rSquared() * 100).toFixed(1)}% variance explained`);

  readonly rSquaredCombinedFormula = computed(() => {
    if (!this.hasData()) {
      return '';
    }
    return buildCombinedCorrelationFormula('rSquared', this.intermediates(), this.measures());
  });

  // ---------------------------------------------------------------------------
  // Regression equation
  // ---------------------------------------------------------------------------

  readonly slope = computed(() => asNumber(this.measures().get('slope')));
  readonly intercept = computed(() => asNumber(this.measures().get('intercept')));

  readonly equationLatex = computed(() => {
    const a = this.slope();
    const b = this.intercept();
    const aStr = fmtCoeff(a);
    if (b >= 0) {
      return `$y \\;=\\; ${aStr}x \\;+\\; ${fmtCoeff(b)}$`;
    }
    return `$y \\;=\\; ${aStr}x \\;-\\; ${fmtCoeff(Math.abs(b))}$`;
  });

  readonly slopeFormatted = computed(() => fmtVal(this.slope()));
  readonly interceptFormatted = computed(() => fmtVal(this.intercept()));

  readonly equationDerivationFormula = computed(() => {
    if (!this.hasData()) {
      return '';
    }
    return buildEquationDerivationFormula(this.intermediates(), this.measures());
  });

  // ---------------------------------------------------------------------------
  // Interpretation
  // ---------------------------------------------------------------------------

  readonly interpretationLines = computed<string[]>(() => {
    if (!this.hasData()) {
      return [];
    }

    const r = this.r();
    const abs = Math.abs(r);
    const a = this.slope();
    const xLbl = this.xLabel();
    const yLbl = this.yLabel();

    const lines: string[] = [];

    // Strength and direction
    if (abs < 0.25) {
      lines.push(`There is no meaningful linear relationship between ${xLbl} and ${yLbl}.`);
    } else {
      const strength = abs >= 0.9 ? 'very strong' : abs >= 0.75 ? 'strong' : abs >= 0.5 ? 'moderate' : 'weak';
      const direction = r > 0 ? 'positive' : 'negative';
      lines.push(`There is a ${strength} ${direction} linear relationship between ${xLbl} and ${yLbl}.`);
    }

    // Slope interpretation
    if (a !== 0) {
      const dir = a > 0 ? 'increases' : 'decreases';
      lines.push(
        `The slope means that for each increase of 1 unit in ${xLbl}, ${yLbl} ${dir} by ${fmtVal(Math.abs(a))} units.`,
      );
    }

    // Intercept interpretation
    const b = this.intercept();
    lines.push(`When ${xLbl} = 0, the model predicts ${yLbl} = ${fmtVal(b)}.`);

    return lines;
  });

  // ---------------------------------------------------------------------------
  // Prediction
  // ---------------------------------------------------------------------------

  readonly predictionXCtrl = new FormControl<number | null>(null);
  readonly predictionResult = signal<{ x: number; y: number; isExtrapolation: boolean } | null>(null);

  readonly canPredict = computed(() => this.predictionXCtrl.value !== null && this.hasData());

  readonly predictionFormula = computed(() => {
    const pred = this.predictionResult();
    if (!pred) {
      return '';
    }

    const a = this.slope();
    const b = this.intercept();
    const x = pred.x;
    const yResult = fmtVal(pred.y);

    const aStr = fmtCoeff(a);
    const xStr = fmtVal(x);
    const bSign = b >= 0 ? '+' : '-';
    const bStr = fmtCoeff(Math.abs(b));

    return `$\\hat{y} \\;=\\; ${aStr} \\times ${xStr} \\;${bSign}\\; ${bStr} \\;=\\; \\mathbf{${yResult}}$`;
  });

  // ---------------------------------------------------------------------------
  // Methods
  // ---------------------------------------------------------------------------

  onSectionMouseEnter(section: CorrelationHoverSection): void {
    this.sectionHover.emit(section);
  }

  onSectionMouseLeave(): void {
    this.sectionHover.emit(null);
  }

  onSectionOpened(section: string): void {
    const next = new Set(this.expandedSections());
    next.add(section);
    this.expandedSections.set(next);
  }

  onSectionClosed(section: string): void {
    const next = new Set(this.expandedSections());
    next.delete(section);
    this.expandedSections.set(next);
  }

  onPredict(): void {
    const x = this.predictionXCtrl.value;
    if (x === null) {
      return;
    }

    const inter = this.intermediates();
    if (!inter.regression) {
      return;
    }

    const y = predictY(inter.regression, x);
    const xMin = Math.min(...inter.xValues);
    const xMax = Math.max(...inter.xValues);
    const isExtrapolation = x < xMin || x > xMax;

    this.predictionResult.set({ x, y, isExtrapolation });
    this.predictionChange.emit({ x, y });
  }

  onClearPrediction(): void {
    this.predictionResult.set(null);
    this.predictionXCtrl.setValue(null);
    this.predictionChange.emit(null);
  }
}

function asNumber(value: number | string | undefined): number {
  if (typeof value === 'number') {
    return value;
  }
  return 0;
}

function fmtVal(v: number): string {
  if (Number.isInteger(v)) {
    return String(v);
  }
  return parseFloat(v.toFixed(4)).toString();
}

function fmtCoeff(v: number): string {
  if (Number.isInteger(v)) {
    return String(v);
  }
  return parseFloat(v.toFixed(3)).toString();
}
